import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";

/** Public store-redirect URL the QR encodes (SPR-021 A-03). */
export const APP_QR_URL = "https://app.twincitysilverbullet.org/app";

/** On-screen preview size. */
const DISPLAY_SIZE = 256;
/** Hidden export size — large enough to print on a sign (≈6.8in @300dpi / ≈13.6in @150dpi). */
const DOWNLOAD_SIZE = 2048;
/** Keep the centered logo the same proportion at both sizes (~22% — safe under level H). */
const DISPLAY_LOGO = 56;
const DOWNLOAD_LOGO = Math.round((DOWNLOAD_SIZE * DISPLAY_LOGO) / DISPLAY_SIZE);

/**
 * AppShare — Admin "App / Share" page (SPR-021 A-03).
 *
 * Renders a downloadable QR code that encodes the public /app store-redirect
 * URL, with the app logo centered and error-correction level H (so the logo
 * overlay doesn't break scanning). A small preview is shown; the DOWNLOAD
 * exports a separate hidden high-resolution (2048px) canvas so the PNG is
 * print-quality for a customer-facing sign.
 */
export default function AppShare() {
  // Wraps the hidden full-resolution QRCodeCanvas used for the PNG export.
  const downloadRef = useRef(null);

  const handleDownload = () => {
    const canvas = downloadRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = url;
    link.download = "tcsb-app-qr.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <main data-testid="admin-app-share-page" className="p-6">
      <header className="mb-8">
        <p className="font-heading tracking-[0.3em] text-teal-deep text-sm">ADMIN</p>
        <h1 className="font-display text-4xl text-charcoal uppercase">App / Share</h1>
        <p className="font-body text-ink/70 mt-1">
          Scan to download the app — print or display this code so customers can grab it.
        </p>
      </header>

      <section
        aria-label="App download QR code"
        className="bg-white border-2 border-charcoal rounded-2xl p-8 shadow-[4px_4px_0_rgba(43,39,36,0.12)] max-w-sm"
      >
        <div className="flex justify-center" data-testid="app-qr-wrapper">
          <QRCodeCanvas
            value={APP_QR_URL}
            size={DISPLAY_SIZE}
            level="H"
            includeMargin
            bgColor="#ffffff"
            fgColor="#2B2724"
            imageSettings={{
              src: "/app-icon-512.png",
              height: DISPLAY_LOGO,
              width: DISPLAY_LOGO,
              excavate: true,
            }}
          />
        </div>

        <p className="font-heading tracking-wider text-charcoal text-center mt-4 uppercase">
          Scan to download the app
        </p>
        <p className="font-body text-ink/50 text-xs text-center break-all mt-1">
          {APP_QR_URL}
        </p>

        <button
          onClick={handleDownload}
          data-testid="download-qr-button"
          className="w-full mt-6 bg-rust text-white font-heading tracking-widest py-2 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 hover:shadow-[0_2px_0_#7f371e] transition-all"
        >
          DOWNLOAD PNG ({DOWNLOAD_SIZE}×{DOWNLOAD_SIZE})
        </button>
        <p className="font-body text-ink/50 text-xs text-center mt-2">
          Downloads a {DOWNLOAD_SIZE}×{DOWNLOAD_SIZE}px PNG — print-ready for a sign.
        </p>
      </section>

      {/* Hidden full-resolution canvas used only for the PNG export (off-screen,
          not display:none, so the canvas still rasterizes for toDataURL). */}
      <div
        ref={downloadRef}
        aria-hidden="true"
        style={{ position: "absolute", left: "-99999px", top: 0, width: 0, height: 0, overflow: "hidden" }}
      >
        <QRCodeCanvas
          value={APP_QR_URL}
          size={DOWNLOAD_SIZE}
          level="H"
          includeMargin
          bgColor="#ffffff"
          fgColor="#2B2724"
          imageSettings={{
            src: "/app-icon-512.png",
            height: DOWNLOAD_LOGO,
            width: DOWNLOAD_LOGO,
            excavate: true,
          }}
        />
      </div>
    </main>
  );
}
